import { Toast, TOAST_TYPE } from 'context/toastContext';
import {
  StateOption,
  DayOfWeek,
  HolidayOption,
  FederalHoliday,
  EntityIDMap,
  TimeZone,
} from 'types';

export const UNKNOWN_ERROR_TOAST: Toast = {
  type: TOAST_TYPE.ERROR,
  message: 'Oops, that was unexpected! Please contact Traxero support. :(',
};

export const BACK_BUTTON_TEXT = 'go back';

// File upload constants
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

// File name constants
export const USER_SIGNATURE_FILENAME = '__AUTURA_PS_UNIQUE_USER_SIGNATURE.png';

export enum PHONES {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

export const THE_DISTANT_FUTURE = 32503698000000;

export const DELIMITER = '\u001F';

export const PERMISSIONABLE_ENTITIES = [
  EntityIDMap.User,
  EntityIDMap.Property,
  EntityIDMap.Property_Group,
  EntityIDMap.Job,
  EntityIDMap.Contact,
  EntityIDMap.Rule,
  EntityIDMap.Ruleset,
  EntityIDMap.Violation,
  EntityIDMap.Vehicle,
  // Entity.Permit,    coming soon
  // Entity.Payment,
  // Entity.Truck,
];

export const STATES_AND_PROVINCES: StateOption[] = [
  // United States
  { stateCode: 'AL', label: 'Alabama', countryCode: 'US' },
  { stateCode: 'AK', label: 'Alaska', countryCode: 'US' },
  { stateCode: 'AZ', label: 'Arizona', countryCode: 'US' },
  { stateCode: 'AR', label: 'Arkansas', countryCode: 'US' },
  { stateCode: 'CA', label: 'California', countryCode: 'US' },
  { stateCode: 'CO', label: 'Colorado', countryCode: 'US' },
  { stateCode: 'CT', label: 'Connecticut', countryCode: 'US' },
  { stateCode: 'DC', label: 'Washington D.C.', countryCode: 'US' },
  { stateCode: 'DE', label: 'Delaware', countryCode: 'US' },
  { stateCode: 'FL', label: 'Florida', countryCode: 'US' },
  { stateCode: 'GA', label: 'Georgia', countryCode: 'US' },
  { stateCode: 'HI', label: 'Hawaii', countryCode: 'US' },
  { stateCode: 'ID', label: 'Idaho', countryCode: 'US' },
  { stateCode: 'IL', label: 'Illinois', countryCode: 'US' },
  { stateCode: 'IN', label: 'Indiana', countryCode: 'US' },
  { stateCode: 'IA', label: 'Iowa', countryCode: 'US' },
  { stateCode: 'KS', label: 'Kansas', countryCode: 'US' },
  { stateCode: 'KY', label: 'Kentucky', countryCode: 'US' },
  { stateCode: 'LA', label: 'Louisiana', countryCode: 'US' },
  { stateCode: 'ME', label: 'Maine', countryCode: 'US' },
  { stateCode: 'MD', label: 'Maryland', countryCode: 'US' },
  { stateCode: 'MA', label: 'Massachusetts', countryCode: 'US' },
  { stateCode: 'MI', label: 'Michigan', countryCode: 'US' },
  { stateCode: 'MN', label: 'Minnesota', countryCode: 'US' },
  { stateCode: 'MS', label: 'Mississippi', countryCode: 'US' },
  { stateCode: 'MO', label: 'Missouri', countryCode: 'US' },
  { stateCode: 'MT', label: 'Montana', countryCode: 'US' },
  { stateCode: 'NE', label: 'Nebraska', countryCode: 'US' },
  { stateCode: 'NV', label: 'Nevada', countryCode: 'US' },
  { stateCode: 'NH', label: 'New Hampshire', countryCode: 'US' },
  { stateCode: 'NJ', label: 'New Jersey', countryCode: 'US' },
  { stateCode: 'NM', label: 'New Mexico', countryCode: 'US' },
  { stateCode: 'NY', label: 'New York', countryCode: 'US' },
  { stateCode: 'NC', label: 'North Carolina', countryCode: 'US' },
  { stateCode: 'ND', label: 'North Dakota', countryCode: 'US' },
  { stateCode: 'OH', label: 'Ohio', countryCode: 'US' },
  { stateCode: 'OK', label: 'Oklahoma', countryCode: 'US' },
  { stateCode: 'OR', label: 'Oregon', countryCode: 'US' },
  { stateCode: 'PA', label: 'Pennsylvania', countryCode: 'US' },
  { stateCode: 'PR', label: 'Puerto Rico', countryCode: 'US' },
  { stateCode: 'RI', label: 'Rhode Island', countryCode: 'US' },
  { stateCode: 'SC', label: 'South Carolina', countryCode: 'US' },
  { stateCode: 'SD', label: 'South Dakota', countryCode: 'US' },
  { stateCode: 'TN', label: 'Tennessee', countryCode: 'US' },
  { stateCode: 'TX', label: 'Texas', countryCode: 'US' },
  { stateCode: 'UT', label: 'Utah', countryCode: 'US' },
  { stateCode: 'VT', label: 'Vermont', countryCode: 'US' },
  { stateCode: 'VA', label: 'Virginia', countryCode: 'US' },
  { stateCode: 'VI', label: 'Virgin Islands', countryCode: 'US' },
  { stateCode: 'WA', label: 'Washington', countryCode: 'US' },
  { stateCode: 'WV', label: 'West Virginia', countryCode: 'US' },
  { stateCode: 'WI', label: 'Wisconsin', countryCode: 'US' },
  { stateCode: 'WY', label: 'Wyoming', countryCode: 'US' },
  // Canada
  { stateCode: 'AB', label: 'Alberta', countryCode: 'CA' },
  { stateCode: 'BC', label: 'British Columbia', countryCode: 'CA' },
  { stateCode: 'MB', label: 'Manitoba', countryCode: 'CA' },
  { stateCode: 'NB', label: 'New Brunswick', countryCode: 'CA' },
  { stateCode: 'NL', label: 'Newfoundland and Labrador', countryCode: 'CA' },
  { stateCode: 'NS', label: 'Nova Scotia', countryCode: 'CA' },
  { stateCode: 'ON', label: 'Ontario', countryCode: 'CA' },
  { stateCode: 'PE', label: 'Prince Edward Island', countryCode: 'CA' },
  { stateCode: 'QC', label: 'Quebec', countryCode: 'CA' },
  { stateCode: 'SK', label: 'Saskatchewan', countryCode: 'CA' },
  { stateCode: 'NT', label: 'Northwest Territories', countryCode: 'CA' },
  { stateCode: 'NU', label: 'Nunavut', countryCode: 'CA' },
  { stateCode: 'YT', label: 'Yukon', countryCode: 'CA' },
  // Mexico
  { stateCode: 'AGU', label: 'Aguascalientes', countryCode: 'MX' },
  { stateCode: 'BCN', label: 'Baja California', countryCode: 'MX' },
  { stateCode: 'BCS', label: 'Baja California Sur', countryCode: 'MX' },
  { stateCode: 'CAM', label: 'Campeche', countryCode: 'MX' },
  { stateCode: 'CHP', label: 'Chiapas', countryCode: 'MX' },
  { stateCode: 'CHH', label: 'Chihuahua', countryCode: 'MX' },
  { stateCode: 'COA', label: 'Coahuila', countryCode: 'MX' },
  { stateCode: 'COL', label: 'Colima', countryCode: 'MX' },
  { stateCode: 'DUR', label: 'Durango', countryCode: 'MX' },
  { stateCode: 'GUA', label: 'Guanajuato', countryCode: 'MX' },
  { stateCode: 'GRO', label: 'Guerrero', countryCode: 'MX' },
  { stateCode: 'HID', label: 'Hidalgo', countryCode: 'MX' },
  { stateCode: 'JAL', label: 'Jalisco', countryCode: 'MX' },
  { stateCode: 'MEX', label: 'Mexico', countryCode: 'MX' },
  { stateCode: 'MIC', label: 'Michoacán', countryCode: 'MX' },
  { stateCode: 'MOR', label: 'Morelos', countryCode: 'MX' },
  { stateCode: 'NAY', label: 'Nayarit', countryCode: 'MX' },
  { stateCode: 'NLE', label: 'Nuevo León', countryCode: 'MX' },
  { stateCode: 'OAX', label: 'Oaxaca', countryCode: 'MX' },
  { stateCode: 'PUE', label: 'Puebla', countryCode: 'MX' },
  { stateCode: 'QUE', label: 'Querétaro', countryCode: 'MX' },
  { stateCode: 'ROO', label: 'Quintana Roo', countryCode: 'MX' },
  { stateCode: 'SLP', label: 'San Luis Potosí', countryCode: 'MX' },
  { stateCode: 'SIN', label: 'Sinaloa', countryCode: 'MX' },
  { stateCode: 'SON', label: 'Sonora', countryCode: 'MX' },
  { stateCode: 'TAB', label: 'Tabasco', countryCode: 'MX' },
  { stateCode: 'TAM', label: 'Tamaulipas', countryCode: 'MX' },
  { stateCode: 'TLA', label: 'Tlaxcala', countryCode: 'MX' },
  { stateCode: 'VER', label: 'Veracruz', countryCode: 'MX' },
  { stateCode: 'YUC', label: 'Yucatán', countryCode: 'MX' },
  { stateCode: 'ZAC', label: 'Zacatecas', countryCode: 'MX' },
];

export const DaysOfWeek = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
];

export const FEDERAL_HOLIDAYS: Array<HolidayOption> = [
  {
    label: FederalHoliday.NEW_YEARS_DAY,
    countryCodes: ['US', 'CA'],
  },
  {
    label: FederalHoliday.MARTIN_LUTHER_KING_JR_DAY,
    countryCodes: ['US'],
  },
  {
    label: FederalHoliday.PRESIDENTS_DAY,
    countryCodes: ['US'],
  },
  {
    label: FederalHoliday.MEMORIAL_DAY,
    countryCodes: ['US'],
  },
  {
    label: FederalHoliday.JUNETEENTH,
    countryCodes: ['US'],
  },
  {
    label: FederalHoliday.INDEPENDENCE_DAY,
    countryCodes: ['US'],
  },
  {
    label: FederalHoliday.LABOR_DAY,
    countryCodes: ['US'],
  },
  {
    label: FederalHoliday.VETERANS_DAY,
    countryCodes: ['US'],
  },
  {
    label: FederalHoliday.THANKSGIVING_US,
    countryCodes: ['US'],
  },
  {
    label: FederalHoliday.CHRISTMAS_DAY,
    countryCodes: ['US', 'CA'],
  },
  {
    label: FederalHoliday.GOOD_FRIDAY,
    countryCodes: ['CA'],
  },
  {
    label: FederalHoliday.VICTORIA_DAY,
    countryCodes: ['CA'],
  },
  {
    label: FederalHoliday.CANADA_DAY,
    countryCodes: ['CA'],
  },
  {
    label: FederalHoliday.CIVIC_HOLIDAY,
    countryCodes: ['CA'],
  },
  {
    label: FederalHoliday.LABOUR_DAY,
    countryCodes: ['CA'],
  },
  {
    label: FederalHoliday.TRUTH_AND_RECONCILIATION_DAY,
    countryCodes: ['CA'],
  },
  {
    label: FederalHoliday.THANKSGIVING_CA,
    countryCodes: ['CA'],
  },
  {
    label: FederalHoliday.REMEMBRANCE_DAY,
    countryCodes: ['CA'],
  },
  {
    label: FederalHoliday.BOXING_DAY,
    countryCodes: ['CA'],
  },
];

export const TIME_ZONES: TimeZone[] = [
  { label: 'America/Boise', timeZone: 'America/Boise' },
  { label: 'America/Chicago', timeZone: 'America/Chicago' },
  { label: 'America/Denver', timeZone: 'America/Denver' },
  { label: 'America/Detroit', timeZone: 'America/Detroit' },
  { label: 'America/Los_Angeles', timeZone: 'America/Los_Angeles' },
  { label: 'America/New_York', timeZone: 'America/New_York' },
  { label: 'America/Phoenix', timeZone: 'America/Phoenix' },
  { label: 'America/Toronto', timeZone: 'America/Toronto' },
  { label: 'America/Vancouver', timeZone: 'America/Vancouver' },
  { label: 'America/Anchorage', timeZone: 'America/Anchorage' },
  { label: 'America/Puerto_Rico', timeZone: 'America/Puerto_Rico' },
  {
    label: 'America/Kentucky/Louisville',
    timeZone: 'America/Kentucky/Louisville',
  },
  {
    label: 'America/Kentucky/Monticello',
    timeZone: 'America/Kentucky/Monticello',
  },
  { label: 'America/Adak', timeZone: 'America/Adak' },
  {
    label: 'America/Indiana/Indianapolis',
    timeZone: 'America/Indiana/Indianapolis',
  },
  { label: 'America/Indiana/Knox', timeZone: 'America/Indiana/Knox' },
  { label: 'America/Indiana/Marengo', timeZone: 'America/Indiana/Marengo' },
  {
    label: 'America/Indiana/Petersburg',
    timeZone: 'America/Indiana/Petersburg',
  },
  { label: 'America/Indiana/Tell_City', timeZone: 'America/Indiana/Tell_City' },
  { label: 'America/Indiana/Vevay', timeZone: 'America/Indiana/Vevay' },
  { label: 'America/Indiana/Vincennes', timeZone: 'America/Indiana/Vincennes' },
  { label: 'America/Indiana/Winamac', timeZone: 'America/Indiana/Winamac' },

  { label: 'America/Menominee', timeZone: 'America/Menominee' },
  { label: 'America/Nome', timeZone: 'America/Nome' },
  {
    label: 'America/North_Dakota/Beulah',
    timeZone: 'America/North_Dakota/Beulah',
  },
  {
    label: 'America/North_Dakota/Center',
    timeZone: 'America/North_Dakota/Center',
  },
  {
    label: 'America/North_Dakota/New_Salem',
    timeZone: 'America/North_Dakota/New_Salem',
  },
  { label: 'America/Sitka', timeZone: 'America/Sitka' },
  { label: 'America/Yakutat', timeZone: 'America/Yakutat' },
  { label: 'America/Atikokan', timeZone: 'America/Atikokan' },
  { label: 'America/Blanc-Sablon', timeZone: 'America/Blanc-Sablon' },
  { label: 'America/Cambridge_Bay', timeZone: 'America/Cambridge_Bay' },
  { label: 'America/Coral_Harbour', timeZone: 'America/Coral_Harbour' },
  { label: 'America/Creston', timeZone: 'America/Creston' },
  { label: 'America/Dawson', timeZone: 'America/Dawson' },
  { label: 'America/Dawson_Creek', timeZone: 'America/Dawson_Creek' },
  { label: 'America/Edmonton', timeZone: 'America/Edmonton' },
  { label: 'America/Fort_Nelson', timeZone: 'America/Fort_Nelson' },
  { label: 'America/Glace_Bay', timeZone: 'America/Glace_Bay' },
  { label: 'America/Goose_Bay', timeZone: 'America/Goose_Bay' },
  { label: 'America/Halifax', timeZone: 'America/Halifax' },
  { label: 'America/Inuvik', timeZone: 'America/Inuvik' },
  { label: 'America/Iqaluit', timeZone: 'America/Iqaluit' },
  { label: 'America/Moncton', timeZone: 'America/Moncton' },
  { label: 'America/Montreal', timeZone: 'America/Montreal' },
  { label: 'America/Rankin_Inlet', timeZone: 'America/Rankin_Inlet' },
  { label: 'America/Regina', timeZone: 'America/Regina' },
  { label: 'America/Resolute', timeZone: 'America/Resolute' },
  { label: 'America/St_Johns', timeZone: 'America/St_Johns' },
  { label: 'America/Swift_Current', timeZone: 'America/Swift_Current' },
  { label: 'America/Whitehorse', timeZone: 'America/Whitehorse' },
  { label: 'America/Winnipeg', timeZone: 'America/Winnipeg' },
];
